/**
 * J5 (ALL-5643 / ALL-4839): display formatting for a transfer amount.
 *
 * The gateway sends amounts as decimal strings (an 18-decimal chain amount
 * cannot survive a JS number round-trip), so this formats the string itself.
 * The amount is never parsed with `parseFloat`/`Number` — passing it through a
 * float could silently round a value like `0.123456789012345678` and would
 * show the operator a number the chain never moved.
 */

/** A decimal amount, optionally signed, with optional fraction: `-1000.50`. */
const DECIMAL_AMOUNT = /^([+-]?)(\d+)(?:\.(\d*))?$/;

/** What we render when there is no usable amount. Never a placeholder number. */
export const UNKNOWN_AMOUNT = "—";

/**
 * Format a wire amount for display, e.g. `1000.50000000` + `ETH` → `1,000.50 ETH`.
 *
 * - full precision is kept (the string is never converted to a number);
 * - trailing zeros beyond 2 decimals are trimmed, but at least 2 are kept;
 * - the integer part gets thousands separators, the fraction never does;
 * - an unparseable or missing amount renders as `—` rather than a made-up value.
 */
export function formatTransferAmount(amount: string | null | undefined, symbol?: string | null): string {
  const raw = typeof amount === "string" ? amount.trim().replace(/,/g, "") : "";
  const match = DECIMAL_AMOUNT.exec(raw);
  if (!match) return UNKNOWN_AMOUNT;

  const [, sign, integerDigits, fractionDigits = ""] = match;

  // Keep precision, drop only the padding zeros the chain adds past 2 decimals.
  let fraction = fractionDigits;
  while (fraction.length > 2 && fraction.endsWith("0")) fraction = fraction.slice(0, -1);
  fraction = fraction.padEnd(2, "0");

  // `007` is the same amount as `7`; keep a lone `0` so `0.50` stays `0.50`.
  const integer = integerDigits.replace(/^0+(?=\d)/, "");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formatted = `${sign === "-" ? "-" : ""}${grouped}.${fraction}`;
  const asset = typeof symbol === "string" ? symbol.trim() : "";
  return asset ? `${formatted} ${asset}` : formatted;
}
